import { Link } from "react-router-dom";
import { Wrench, MapPin, Calendar, EyeOff } from "lucide-react";
import PhaseChip from "./PhaseChip";
import { JCProgressBar } from "./JCProgress";

/**
 * <JobCardRow /> — The ONE canonical compact JC row.
 *
 * Wave 25.16 Phase 3 / Wave 26.0 hard-lock:
 *   This is the only sanctioned way to render a Job Card preview
 *   anywhere in the app (lists, dashboards, portals, calendar
 *   popovers). The ESLint rule `no-inline-jobcard-row` fails any other
 *   file that tries to render `j.short_id + j.phase + j.title` inline.
 *
 * Visual contract (locked, see snapshot test):
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ 🛠 #ABC123  Title                       [PHASE]            ✕ │
 *   │     customer · 📍 site · 📅 12 Feb 14:30                      │
 *   │     [████████░░░░░░░] 53% complete                            │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Props:
 *   job              — JC object (required: id, title)
 *   customer         — { id, name } | null. Renders as a link to /customers/:id
 *   site             — { name } | null
 *   to               — link target. Default `/jobcards/${job.id}`. Pass
 *                      `null` to disable the wrapping link (read-only
 *                      portal use).
 *   compact          — bool. Tighter padding for sidebar widgets.
 *   showProgress     — bool. Default true. Hide on dashboards that
 *                      already overview status another way.
 *   showSchedule     — bool. Default true.
 *   showCustomer     — bool. Default true.
 *   showSite         — bool. Default true.
 *   showPhaseChip    — bool. Default true. Surfaces that group BY phase
 *                      (e.g. JobCardsBlock) should set false to avoid
 *                      duplication.
 *   accent           — CSS color string for the icon + hover border.
 *                      Default the JC orange.
 *   actions          — node. Right-side action buttons rendered as
 *                      siblings to the link (e.g. print / delete).
 *                      Each must call e.preventDefault() to avoid the
 *                      wrapping link firing.
 *   className        — passthrough wrapper class
 *   testIdPrefix     — for stable data-testids across tests
 */
export default function JobCardRow({
  job,
  customer = null,
  site = null,
  to,
  compact = false,
  showProgress = true,
  showSchedule = true,
  showCustomer = true,
  showSite = true,
  showPhaseChip = true,
  accent = "#F59E0B",
  actions = null,
  onClick = null,
  className = "",
  testIdPrefix = "jc-row",
}) {
  if (!job) return null;
  const { id, title, short_id, phase, scheduled_at, checklist, hidden_from_client } = job;
  const linkTarget = to === undefined ? `/jobcards/${id}` : to;
  const padCls = compact ? "px-2 py-1.5" : "px-3 py-2";
  const titleCls = compact ? "text-[12px]" : "text-sm";

  // The clickable area — wraps title/customer/site/schedule/progress.
  // Priority: explicit `to` link > `onClick` button > read-only div.
  const Wrap = linkTarget
    ? ({ children, ...props }) => (
        <Link to={linkTarget} {...props} data-testid={`${testIdPrefix}-${id}-link`}>
          {children}
        </Link>
      )
    : onClick
    ? ({ children, className: cls, ...props }) => (
        <button
          type="button"
          onClick={(e) => onClick(e, job)}
          className={`${cls} text-left w-full`}
          data-testid={`${testIdPrefix}-${id}-button`}
          {...props}
        >
          {children}
        </button>
      )
    : ({ children, ...props }) => <div {...props}>{children}</div>;

  return (
    <div
      className={`group flex items-stretch gap-1 rounded border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/15 transition ${className}`}
      data-testid={`${testIdPrefix}-${id}`}
      style={{ borderColor: undefined }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${accent}55`; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = ""; }}
    >
      <Wrap className={`flex-1 min-w-0 flex items-start gap-2 ${padCls}`}>
        <Wrench
          className="w-3.5 h-3.5 mt-0.5 shrink-0"
          style={{ color: accent }}
        />
        <div className="flex-1 min-w-0">
          {/* Top row: short_id + title */}
          <div className="flex items-center gap-2 min-w-0">
            {short_id && (
              <span className="font-mono text-[10px] text-[#1FB6C1] shrink-0">
                #{short_id}
              </span>
            )}
            <span
              className={`${titleCls} font-medium text-white truncate`}
              data-testid={`${testIdPrefix}-${id}-title`}
            >
              {title || "(untitled job)"}
            </span>
            {hidden_from_client && (
              <span className="text-[9px] uppercase tracking-wider text-rose-300 inline-flex items-center gap-0.5 shrink-0">
                <EyeOff className="w-2.5 h-2.5" /> hidden
              </span>
            )}
          </div>
          {/* Middle row: customer / site / time */}
          {((showCustomer && customer) || (showSite && site?.name) || (showSchedule && scheduled_at)) && (
            <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-[10px] text-[#a0aabf]">
              {showCustomer && customer && (
                <span
                  className="truncate"
                  data-testid={`${testIdPrefix}-${id}-customer`}
                >
                  {customer.name}
                </span>
              )}
              {showSite && site?.name && (
                <span className="inline-flex items-center gap-0.5 truncate">
                  <MapPin className="w-2.5 h-2.5" />
                  {site.name}
                </span>
              )}
              {showSchedule && scheduled_at && (
                <span className="inline-flex items-center gap-0.5 font-mono">
                  <Calendar className="w-2.5 h-2.5" />
                  {new Date(scheduled_at).toLocaleString("en-GB", {
                    day: "numeric", month: "short",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          )}
          {/* Bottom row: progress bar */}
          {showProgress && Array.isArray(checklist) && checklist.length > 0 && (
            <div className="mt-1.5 max-w-[260px]">
              <JCProgressBar tasks={checklist} compact />
            </div>
          )}
        </div>
        {showPhaseChip && phase && (
          <PhaseChip value={phase} className="!text-[9px] !py-0 !px-1.5 shrink-0 mt-0.5" />
        )}
      </Wrap>
      {actions && (
        <div className="flex items-center gap-0.5 pr-2 shrink-0" data-testid={`${testIdPrefix}-${id}-actions`}>
          {actions}
        </div>
      )}
    </div>
  );
}
